import CryptoJS from 'crypto-js';

const ENCRYPTION_KEY_STORAGE_NAME = 'encryption_key';
let isEncryptionEnabled = false;
let currentCompanyId: string | null = null;

export const setEncryptionEnabled = (enabled: boolean): void => {
    isEncryptionEnabled = enabled;
};

export const setCurrentCompanyId = (id: string | null): void => {
    currentCompanyId = id;
};

export const getEncryptionKey = (companyId?: string | null): string | null => {
    const id = companyId || currentCompanyId;
    if (!id) return null;
    return localStorage.getItem(`${ENCRYPTION_KEY_STORAGE_NAME}_${id}`);
};

export const setEncryptionKey = (key: string, companyId?: string | null): void => {
    const id = companyId || currentCompanyId;
    if (!id) return;
    localStorage.setItem(`${ENCRYPTION_KEY_STORAGE_NAME}_${id}`, key);
};

export const hasEncryptionKey = (): boolean => {
    return !!getEncryptionKey();
};

const encrypt = (text: string, key: string): string => {
    try {
        return CryptoJS.AES.encrypt(text, key).toString();
    } catch (error) {
        console.error('Encryption failed:', error);
        return text;
    }
};

const decrypt = (ciphertext: string, key: string): string => {
    try {
        const bytes = CryptoJS.AES.decrypt(ciphertext, key);
        const originalText = bytes.toString(CryptoJS.enc.Utf8);
        if (!originalText) return ciphertext; // Return original if decryption fails (e.g. wrong key or not encrypted)
        return originalText;
    } catch (error) {
        console.error('Decryption failed:', error);
        return ciphertext;
    }
};

export const encryptSensitiveFields = <T extends Record<string, any>>(sensitiveFields: any, data: T | T[]): T | T[] => {
    if (!isEncryptionEnabled) return data;

    const key = getEncryptionKey();
    if (!key) {
        console.warn('No encryption key found. Skipping encryption.');
        return data;
    }

    if (Array.isArray(data)) {
        return data.map(item => encryptSensitiveFields(sensitiveFields, item)) as T[];
    }

    const result = { ...data };
    for (const field in result) {
        if (Object.prototype.hasOwnProperty.call(result, field)) {
            if (sensitiveFields.has(field) && result[field]?.trim()) {
                result[field] = encrypt(result[field], key) as any;
            }
        }
    }
    return result;
};

export const decryptSensitiveFields = <T extends Record<string, any>>(sensitiveFields: any, data: T | T[]): T | T[] => {
    if (!isEncryptionEnabled) return data;

    const key = getEncryptionKey();
    if (!key) {
        console.warn('No encryption key found. Skipping decryption.');
        return data;
    }

    if (Array.isArray(data)) {
        return data.map(item => decryptSensitiveFields(sensitiveFields, item)) as T[];
    }

    const result = { ...data };
    for (const field in result) {
        if (Object.prototype.hasOwnProperty.call(result, field)) {
            if (sensitiveFields.has(field) && result[field]?.trim()) {
                result[field] = decrypt(result[field], key) as any;
            }
        }
    }
    return result;
};
